import { getItem, removeItem, setItem } from 'owa-local-storage';
import { getOrigin } from 'owa-url';
import { getUserEmailAddress } from 'owa-session-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { MillisecondsIn1Hour } from 'mini-datetime-utils/lib/dateValues/constants';
import { trace } from 'owa-trace';

export type ServiceModel = 'todo' | 'todob2';

interface AutoDiscoverResponse {
    Protocol: string;
    Url: string;
}

let discoveryPromise: Promise<ServiceModel>;

const TODO_SERVICE_STORAGE_KEY = 'ToDoServiceModel';
const TWELVE_HOURS_IN_MS = 12 * MillisecondsIn1Hour;

export async function discoverTodoServiceModel(): Promise<ServiceModel> {
    // share a single discovery promise at a time between all overlapping callers
    if (!discoveryPromise) {
        discoveryPromise = discoverTodoServiceModelInternal();
    }
    const result = await discoveryPromise;
    discoveryPromise = undefined;
    return result;
}

async function discoverTodoServiceModelInternal(): Promise<ServiceModel> {
    // check if service value is recently cached
    // this is a performance optimization for the user and a load optimization for AutoDiscover service
    const { model, expiry } = getToDoServiceModelItem() ?? {};
    if (model && expiry) {
        if (isServiceModel(model) && Date.now() < expiry) {
            return model;
        } else {
            removeToDoServiceModelItem();
        }
    }

    // query AutoDiscover endpoint to discover service URL
    try {
        const userIdentity = getUserEmailAddress() || getUserMailboxInfo()?.userIdentity;
        const autoDiscoverUrl = `${getOrigin()}/autodiscover/autodiscover.json/v1.0/${
            userIdentity || getUserEmailAddress()
        }?Protocol=ToDo`;

        const autoDiscoverResponse = await fetch(autoDiscoverUrl, {
            method: 'GET',
            headers: { 'X-AnchorMailbox': userIdentity },
        }).then(response => {
            if (isSuccessStatusCode(response.status)) {
                return response.json() as Promise<AutoDiscoverResponse>;
            } else {
                throw new Error();
            }
        });

        let model: ServiceModel = 'todo';
        const urlModel = new URL(autoDiscoverResponse.Url).pathname.substring(1);
        if (isServiceModel(urlModel)) {
            model = urlModel;
        }
        const expiry = Date.now() + TWELVE_HOURS_IN_MS;

        setToDoServiceModelItem({ model, expiry });
    } catch (error) {
        trace.warn(`[todo-service]: AutoDiscover query with retry failed: ${error}`);
    }
    return (getToDoServiceModelItem() ?? {}).model ?? 'todo';
}

interface ToDoServiceModelItem {
    /** Current To Do service model, e.g. "todo" or "todob2" */
    model: ServiceModel;

    /** Expiry of this cache item, as number of milliseconds elapsed since Unix epoch */
    expiry: number;
}

function getToDoServiceModelItem(): ToDoServiceModelItem | undefined {
    try {
        const storageItem = getItem(window, TODO_SERVICE_STORAGE_KEY);
        return JSON.parse(storageItem) as ToDoServiceModelItem | undefined;
    } catch {
        return undefined;
    }
}

function setToDoServiceModelItem(item: ToDoServiceModelItem) {
    setItem(window, TODO_SERVICE_STORAGE_KEY, JSON.stringify(item));
}

function removeToDoServiceModelItem() {
    removeItem(window, TODO_SERVICE_STORAGE_KEY);
}

function isServiceModel(model: string): model is ServiceModel {
    return ['todo', 'todob2'].includes(model);
}
